import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useIntl } from "react-intl";
import { useUser } from "@clerk/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faLock } from "@fortawesome/pro-light-svg-icons";

/**
 * Full-screen gate for migrated accounts imported with a temporary
 * password (Clerk unsafe_metadata.requirePasswordChange === true).
 *
 * The user must set a new password before reaching the app. On success
 * the flag is cleared and the app renders normally. The temp password
 * (received by email) is required as `currentPassword` by Clerk's
 * updatePassword, which is what actually proves ownership here.
 */
const ForcePasswordChange = () => {
  const { locale } = useIntl();
  const { user } = useUser();
  const fr = locale !== "en-US";

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError(fr ? "8 caractères minimum." : "8 characters minimum.");
      return;
    }
    if (newPassword !== confirm) {
      setError(fr ? "Les mots de passe ne correspondent pas." : "Passwords don't match.");
      return;
    }
    if (!user) return;
    setBusy(true);
    try {
      await user.updatePassword({
        currentPassword,
        newPassword,
        signOutOfOtherSessions: true,
      });
      await user.update({
        unsafeMetadata: { ...user.unsafeMetadata, requirePasswordChange: false },
      });
      // The parent re-renders on user state change and drops this gate.
    } catch (err) {
      const clerkMessage = (err as { errors?: { message?: string }[] })?.errors?.[0]?.message;
      setError(
        clerkMessage ??
          (fr ? "Impossible de mettre à jour le mot de passe." : "Could not update the password.")
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, width: "min(460px, 94vw)", borderRadius: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "primary.main",
              bgcolor: theme => `${theme.palette.primary.main}1a`,
            }}
          >
            <FontAwesomeIcon icon={faLock} />
          </Box>
          <Typography fontSize={20} fontWeight={700}>
            {fr ? "Choisissez votre nouveau mot de passe" : "Choose your new password"}
          </Typography>
        </Box>
        <Typography fontSize={13} color="text.secondary" mb={3}>
          {fr
            ? "Votre compte a été migré vers le nouvel espace ila26. Saisissez le mot de passe temporaire reçu par e-mail, puis définissez le vôtre."
            : "Your account was migrated to the new ila26 workspace. Enter the temporary password you received by email, then set your own."}
        </Typography>

        <form onSubmit={submit}>
          <TextField
            fullWidth
            type={show ? "text" : "password"}
            label={fr ? "Mot de passe temporaire" : "Temporary password"}
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            margin="dense"
            autoComplete="current-password"
          />
          <TextField
            fullWidth
            type={show ? "text" : "password"}
            label={fr ? "Nouveau mot de passe" : "New password"}
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            margin="dense"
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShow(v => !v)} edge="end" size="small">
                    <FontAwesomeIcon icon={show ? faEyeSlash : faEye} fontSize={14} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            type={show ? "text" : "password"}
            label={fr ? "Confirmer le mot de passe" : "Confirm password"}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            margin="dense"
            autoComplete="new-password"
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={busy || !currentPassword || !newPassword || !confirm}
            sx={{ mt: 3, textTransform: "none", borderRadius: 2, py: 1.2 }}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {fr ? "Définir mon mot de passe" : "Set my password"}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default ForcePasswordChange;
